import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { adminApi } from '../services/adminApi';

function AdminPanel({ onClose }) {
  const { getAuthToken } = useAuth();
  const [view, setView] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [activeJob, setActiveJob] = useState(null);
  const [jobType, setJobType] = useState('full');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const pollRef = useRef(null);

  const loadDashboard = useCallback(async () => {
    try {
      const token = await getAuthToken();
      const [statsData, jobsData] = await Promise.all([
        adminApi.getCardStats(token),
        adminApi.listJobs(token),
      ]);
      setStats(statsData);
      setJobs(jobsData.jobs || []);
    } catch (e) {
      setError(e.message);
    }
  }, [getAuthToken]);

  useEffect(() => {
    loadDashboard();
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [loadDashboard]);

  const handleTriggerJob = async () => {
    setLoading(true);
    setError('');
    try {
      const token = await getAuthToken();
      const result = await adminApi.triggerJob(token, { jobType });
      setActiveJob({ job_id: result.job_id, status: 'pending', progress: 0, total: 0, log: [] });
      setView('progress');
      startPolling(result.job_id);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const startPolling = (jobId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const token = await getAuthToken();
        const job = await adminApi.getJobStatus(token, jobId);
        setActiveJob(job);
        if (job.status === 'completed' || job.status === 'failed') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          if (job.status === 'completed') setView('results');
        }
      } catch {
        // silently retry
      }
    }, 3000);
  };

  const viewJobDetails = (job) => {
    setActiveJob(job);
    if (job.status === 'completed' || job.status === 'failed') {
      setView('results');
    } else {
      setView('progress');
      startPolling(job.job_id);
    }
  };

  const progressPercent = activeJob && activeJob.total > 0
    ? Math.round((activeJob.progress / activeJob.total) * 100)
    : 0;

  const statusColor = (status) => {
    const colors = {
      completed: 'bg-success-light text-success',
      failed: 'bg-error-container text-error',
      running: 'bg-primary-light text-primary',
      pending: 'bg-warning-light text-warning',
    };
    return colors[status] || 'bg-surface-container text-on-surface-variant';
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
      <div
        className="relative w-full max-w-lg max-h-[85vh] bg-white rounded-t-3xl sm:rounded-3xl flex flex-col overflow-hidden shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-outline-variant/30">
          <div className="flex items-center gap-3">
            {view !== 'dashboard' && (
              <button onClick={() => { setView('dashboard'); loadDashboard(); }}
                className="text-on-surface-variant hover:text-on-surface transition-colors">
                <span className="material-symbols-outlined">arrow_back</span>
              </button>
            )}
            <h2 className="text-lg font-semibold text-on-surface">Admin Panel</h2>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-surface-container transition-colors">
            <span className="material-symbols-outlined text-on-surface-variant">close</span>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {error && (
            <div className="px-4 py-3 bg-error-container rounded-xl">
              <p className="text-sm text-error">{error}</p>
            </div>
          )}

          {/* Dashboard */}
          {view === 'dashboard' && (
            <>
              <div>
                <h3 className="text-sm font-semibold text-on-surface-variant uppercase tracking-wider mb-3">Card Database</h3>
                {stats ? (
                  <div className="space-y-3">
                    <div className="bg-primary-light rounded-2xl p-4 text-center">
                      <p className="text-3xl font-bold text-primary">{stats.total}</p>
                      <p className="text-sm text-on-surface-variant">Total Cards</p>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      {Object.entries(stats.by_issuer || {}).sort((a, b) => b[1] - a[1]).map(([issuer, count]) => (
                        <div key={issuer} className="flex justify-between items-center p-3 bg-surface-container-low rounded-xl">
                          <span className="text-xs font-medium text-on-surface truncate mr-2">{issuer}</span>
                          <span className="text-sm font-bold text-on-surface">{count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-on-surface-variant">Loading stats...</p>
                )}
              </div>

              <div>
                <h3 className="text-sm font-semibold text-on-surface-variant uppercase tracking-wider mb-3">Run Backfill</h3>
                <div className="flex gap-3">
                  <select value={jobType} onChange={(e) => setJobType(e.target.value)}
                    className="flex-1 px-4 py-3 bg-surface-container-low border border-outline-variant rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                  >
                    <option value="full">Full (API + Scrape + Static)</option>
                    <option value="api_only">API Only</option>
                    <option value="scrape_only">Scrape Only</option>
                  </select>
                  <button onClick={handleTriggerJob} disabled={loading}
                    className="px-5 py-3 bg-primary text-on-primary rounded-2xl text-sm font-semibold hover:bg-primary-dark transition-colors disabled:opacity-50"
                  >
                    {loading ? 'Starting...' : 'Run'}
                  </button>
                </div>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-on-surface-variant uppercase tracking-wider mb-3">Recent Jobs</h3>
                {jobs.length > 0 ? (
                  <div className="space-y-2">
                    {jobs.map((job) => (
                      <button key={job.job_id} onClick={() => viewJobDetails(job)}
                        className="w-full text-left p-3 bg-surface-container-low rounded-xl hover:bg-surface-container transition-colors"
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColor(job.status)}`}>
                            {job.status}
                          </span>
                          <span className="text-xs text-on-surface-variant">{job.job_type}</span>
                        </div>
                        <p className="text-xs text-on-surface-variant">
                          {new Date(job.started_at).toLocaleString()}
                        </p>
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-on-surface-variant">No jobs yet</p>
                )}
              </div>
            </>
          )}

          {/* Progress */}
          {view === 'progress' && activeJob && (
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-on-surface-variant uppercase tracking-wider">Job Progress</h3>
              <div className="space-y-2">
                <div className="h-3 bg-surface-container rounded-full overflow-hidden">
                  <div className="h-full bg-primary rounded-full transition-all duration-500" style={{ width: `${progressPercent}%` }} />
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-on-surface">{activeJob.progress || 0} / {activeJob.total || '?'}</span>
                  <span className="text-sm font-semibold text-primary">{progressPercent}%</span>
                </div>
                <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${statusColor(activeJob.status)}`}>
                  {activeJob.status}
                </span>
              </div>
              <div>
                <h4 className="text-xs font-semibold text-on-surface-variant uppercase mb-2">Log</h4>
                <div className="bg-inverse-surface rounded-xl p-3 max-h-48 overflow-y-auto">
                  {(activeJob.log || []).map((entry, i) => (
                    <p key={i} className="text-xs text-inverse-on-surface font-mono leading-relaxed">{entry}</p>
                  ))}
                  {activeJob.status === 'running' && (
                    <p className="text-xs text-inverse-on-surface/50 font-mono animate-pulse">Waiting for updates...</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Results */}
          {view === 'results' && activeJob && (
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-on-surface-variant uppercase tracking-wider">Job Results</h3>
              <div className={`p-4 rounded-2xl text-center text-sm font-semibold ${
                activeJob.status === 'completed' ? 'bg-success-light text-success' : 'bg-error-container text-error'
              }`}>
                {activeJob.status === 'completed' ? 'Job completed successfully' : 'Job failed'}
              </div>

              {activeJob.results && (
                <div>
                  {activeJob.results.error ? (
                    <div className="p-3 bg-error-container rounded-xl">
                      <p className="text-sm text-error">{activeJob.results.error}</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-4 gap-2">
                      {[
                        { label: 'New', value: activeJob.results.new || 0, color: 'text-success' },
                        { label: 'Updated', value: activeJob.results.updated || 0, color: 'text-primary' },
                        { label: 'Same', value: activeJob.results.unchanged || 0, color: 'text-on-surface-variant' },
                        { label: 'Failed', value: activeJob.results.failed || 0, color: 'text-error' },
                      ].map((item) => (
                        <div key={item.label} className="bg-surface-container-low rounded-xl p-3 text-center">
                          <p className={`text-xl font-bold ${item.color}`}>{item.value}</p>
                          <p className="text-xs text-on-surface-variant">{item.label}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {activeJob.completed_at && (
                <p className="text-xs text-on-surface-variant text-center">
                  Completed at {new Date(activeJob.completed_at).toLocaleString()}
                  {activeJob.results?.duration_seconds && ` in ${activeJob.results.duration_seconds.toFixed(1)}s`}
                </p>
              )}

              {activeJob.log && activeJob.log.length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold text-on-surface-variant uppercase mb-2">Log</h4>
                  <div className="bg-inverse-surface rounded-xl p-3 max-h-48 overflow-y-auto">
                    {activeJob.log.map((entry, i) => (
                      <p key={i} className="text-xs text-inverse-on-surface font-mono leading-relaxed">{entry}</p>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminPanel;
